import segment from '@analytics/segment'
import type { SegmentioSettings } from '@segment/analytics-next/dist/types/plugins/segmentio'
import { proxyServiceURLOrigin } from '@/services/Partytown'
import { ECOMMERCE_EVENTS, EcommPayload } from '@/utils/analytics/ecommerce-events'
import { namedLogger } from '../../utils'
import { CustomFacebookPixels } from './custom-facebook-pixels'
import { generateCustomFacebookPixel } from './plugins/custom-facebook-pixel-plugin'
import { KochavaPlugin } from './plugins/kochava-plugin'

const logger = namedLogger('segment Analytics Plugin', {
  debug: false,
})

// Batching configuration for Segment
// This reduces API calls by batching events before sending
const SEGMENT_BATCHING_CONFIG = {
  integrations: {
    'Segment.io': {
      deliveryStrategy: {
        strategy: 'batching',
        config: {
          size: 20, // Batch up to 20 events before sending
          timeout: 10000, // Or send after 10 seconds, whichever comes first
        },
      },
    } satisfies Pick<SegmentioSettings, 'deliveryStrategy'>,
  },
}

export function segmentPlugin() {
  const _segmentPlugin = segment({
    syncAnonymousId: true,
    writeKey: process.env.NEXT_PUBLIC_SEGMENT_KEY,
    // eslint-disable-next-line prettier/prettier
    customScriptSrc: `${proxyServiceURLOrigin}/segment/analytics.js/v1/${process.env.NEXT_PUBLIC_SEGMENT_KEY}/analytics.min.js`,
    // Pass batching configuration to the underlying analytics.js
    ...SEGMENT_BATCHING_CONFIG,
  })

  logger.debug('_segmentPlugin', { _segmentPlugin })

  const plugins = [
    ...Object.entries(CustomFacebookPixels).map(([pixelName, pixelSettings]) => {
      logger.debug('registering pixel', { pixelName })
      return generateCustomFacebookPixel(pixelName, pixelSettings)
    }),
    KochavaPlugin({ appGuid: process.env.NEXT_PUBLIC_KOCHAVA_APP_GUID }),
  ]

  return {
    ..._segmentPlugin,
    loaded: (...args: unknown[]) => {
      try {
        const results = [
          _segmentPlugin?.loaded?.(...args),
          ...plugins.map((plugin) => {
            const loaded = plugin.isLoaded()
            logger.debug('plugin loaded', { loaded })
            return loaded
          }),
        ]
        logger.debug('results', { results })
        return results.every((result) => result)
      } catch (error) {
        logger.error('Error in loaded', undefined, error)
        return false
      }
    },
    ready: (...args: unknown[]) => {
      try {
        _segmentPlugin?.ready?.(...args)

        const results = [...plugins.map((plugin) => plugin.isLoaded())]
        logger.debug('results', { results })
        return results.every((result) => result)
      } catch (error) {
        logger.error('Error in ready', undefined, error)
        return false
      }
    },
    initialize: (...args: unknown[]) => {
      _segmentPlugin.initialize(...args)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.analytics?.ready?.(() => {
        plugins.forEach((plugin) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window.analytics?.register?.(plugin)
        })
      })
    },
    track: (params: {
      payload: {
        event: string
        userId?: string
        anonymousId?: string
        properties: {
          experiments?: unknown
          funnel?: EcommPayload['funnel']
          projectSlug?: string
        }
      }
      config?: {
        disableAnonymousTraffic?: boolean
      }
    }) => {
      // node_modules/@analytics/segment/lib/analytics-plugin-segment.es.js:127 track()
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const client = typeof window !== 'undefined' ? window.analytics : null
      if (!client) {
        return _segmentPlugin.track(params)
      }
      const payload = params.payload,
        config = params.config
      const userId = payload.userId,
        anonymousId = payload.anonymousId

      if (!userId && !anonymousId) {
        const error = new Error('Missing userId and anonymousId. You must include one to make segment call')
        logger.error(error.message, undefined, error)
        throw error
      }

      const data = {
        event: payload.event,
        properties: payload.properties,
        ...(userId ? { userId } : { anonymousId }),
      }

      if (config?.disableAnonymousTraffic && !userId) {
        return false
      }

      client.track(data, {
        integrations: {
          // Custom Facebook Pixels block ecommerce spec events if enabled on plugin
          ...(ECOMMERCE_EVENTS.includes(params?.payload?.event as (typeof ECOMMERCE_EVENTS)[number])
            ? (Object.fromEntries(
                Object.entries(CustomFacebookPixels)
                  .filter(([, pixelSettings]) => pixelSettings.allowEcommerceSpecEvents !== false)
                  .map(([pixelName]) => [pixelName, false]),
              ) as object)
            : {}),
          // Facebook Pixel block ecommerce spec events if enabled
          // "Facebook Pixel" => Streaming Facebook Pixel configured in segment
          ...(ECOMMERCE_EVENTS.includes(params?.payload?.event as (typeof ECOMMERCE_EVENTS)[number]) &&
          process.env.NEXT_PUBLIC_STREAMING_PIXEL_CUSTOM_FACEBOOK_PIXEL_BLOCK_ECOMMERCE_SPEC_EVENTS_ENABLED === 'true'
            ? {
                'Facebook Pixel': false,
              }
            : {}),
        },
      })

      if (
        !!params.payload.event &&
        !!params?.payload?.properties?.funnel &&
        ECOMMERCE_EVENTS.includes(params?.payload?.event as (typeof ECOMMERCE_EVENTS)[number])
      ) {
        const eventName = `${params.payload.event} - ${params.payload.properties.funnel}`
        client.track({ ...data, event: eventName })
      }
    },
    reset: () => {
      _segmentPlugin.reset()
      plugins.forEach((plugin) => {
        if ('reset' in plugin && typeof plugin.reset === 'function') {
          plugin.reset()
        }
      })
    },
  }
}
